import { useState } from 'react';
import { Link } from 'react-router';
import {
  Anchor,
  Button,
  Card,
  Group,
  Loader,
  Modal,
  Select,
  SegmentedControl,
  Stack,
  Table,
  Text,
} from '@mantine/core';
import type { PtwConfigOverview, PtwKontrolOzetiDto } from '@isg/shared-types';
import {
  PTW_KONTROL_AILELERI,
  PTW_KONTROL_AILESI_BASLIGI,
  PTW_KONTROL_AILESI_ETIKETI,
  aileMaliyetMetni,
  kontrolMatrisiHucreleri,
  ptwMaliyetAilesiMi,
} from '@isg/validation';
import type {
  PtwKontrolAilesi,
  PtwKontrolHucreKonumu,
  PtwKontrolMatrisYazma,
} from '@isg/validation';
import {
  useAyarDefteri,
  useMyAuthorization,
  usePtwAileMaliyeti,
  usePtwKontrolMatrisiYaz,
} from '../api/queries';
import { SAPKA_ETIKET } from '../i18n/tr';
import { zamanGoster } from '../lib/format';
import { menuSatiriGorunur, yuzeyGorunur } from '../shell/menu';
import { Sayfalama } from '../sozlesme/Liste';
import { ANLAM } from '../theme';
import { QueryStatus } from './parts';

/**
 * DİLİM 2 — PTW KONTROL MATRİSİ YÜZEYLERİ (Kapsam §16.7.5 ✏️ · §14 K-73a…g ·
 * §23/6-7 ✏️).
 *
 * ⚠️ **EKRAN HÜCREYİ HESAPLAMAZ, ÇÖZER:** hücrenin geçerli değeri motorla AYNI
 * saf fonksiyondan (`kontrolMatrisiHucreleri`) gelir; ekran ikinci bir "aile
 * açık mı" kuralı yazmaz. Pano sayıları sunucudadır (`ptwKontrolOzeti`).
 * ⚠️ **ÜÇ KONUM, İKİ KONUMA İNDİRİLMEZ (K-73b):** işyeri görünümünde *devral*
 * ayrı seçenektir ve holding değeri soluk yazılır — holding sonradan açınca
 * işyerinin "kapalı sandığı" hücre açılmamalı.
 */

const DEGER_ETIKETI = { acik: 'Açık', kapali: 'Kapalı' } as const;

type HucreDurumu = ReturnType<typeof kontrolMatrisiHucreleri>[PtwKontrolAilesi];

/** Kontrol matrisi kartı — PTW yapılandırma sayfasında, tür → zorunlu görev matrisinin altında. */
export function KontrolMatrisiKarti({ config }: { config: PtwConfigOverview }): React.JSX.Element {
  const authz = useMyAuthorization();
  const yaz = usePtwKontrolMatrisiYaz();
  const [kapsam, setKapsam] = useState<string>('holding');
  const workplaceId = kapsam === 'holding' ? null : kapsam;
  // Yazma kapısı sunucudadır (holding_admin — §20.5); izleyici bu sayfayı
  // görür ama yazamaz: kontrol salt okunur çizilir (§2.40 — basılınca 403
  // veren kontrol yerine).
  const yazabilir = authz.isSuccess && !authz.data.saltOkunur;

  // ✏️ MADDE 248 / K-248: eğitim ya da sağlık ailesini AÇAN yazma önce MALİYET
  // ÖZETİNİ gösterir ve onay ister (ikinci düzey düğme açılmaz). Özet tıklama
  // anında sunucuda hesaplanır, KAYDEDİLMEZ; Vazgeç hiçbir şey yazmaz.
  const maliyet = usePtwAileMaliyeti();
  const [bekleyen, setBekleyen] = useState<{
    girdi: PtwKontrolMatrisYazma;
    turAd: string;
  } | null>(null);
  const kapat = (): void => {
    setBekleyen(null);
    maliyet.reset();
  };

  const hucreDegistir = (
    tur: { id: string; ad: string },
    aile: PtwKontrolAilesi,
    konum: string,
    hucre: HucreDurumu,
  ): void => {
    const girdi: PtwKontrolMatrisYazma = {
      kapsam: workplaceId === null ? 'holding' : 'isyeri',
      workplaceId,
      izinTuruRefId: tur.id,
      aile,
      konum: konum as PtwKontrolHucreKonumu,
    };
    // Yazmadan SONRAKİ geçerli değer: devral holding değerini alır — devralın
    // aileyi fiilen açtığı durum da "açmak"tır.
    const sonrakiGecerli = konum === 'devral' ? hucre.holding : konum;
    if (ptwMaliyetAilesiMi(aile) && hucre.gecerli !== 'acik' && sonrakiGecerli === 'acik') {
      setBekleyen({ girdi, turAd: tur.ad });
      maliyet.mutate({ aile, workplaceId });
      return;
    }
    yaz.mutate(girdi);
  };

  return (
    <Card withBorder data-testid="kontrol-matrisi">
      <Stack gap="xs">
        <Text fw={600}>Kontrol matrisi</Text>
        <Select
          label="Kapsam"
          data={[
            { value: 'holding', label: 'Holding varsayılanı' },
            ...config.isyerleri.map((isyeri) => ({ value: isyeri.id, label: isyeri.ad })),
          ]}
          value={kapsam}
          allowDeselect={false}
          onChange={(deger) => deger && setKapsam(deger)}
          data-testid="kontrol-matrisi-kapsam"
          searchable
        />
        <div style={{ overflowX: 'auto' }}>
          <Table withTableBorder data-testid="kontrol-matrisi-tablosu">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>İzin türü</Table.Th>
                {PTW_KONTROL_AILELERI.map((aile) => (
                  <Table.Th key={aile}>{PTW_KONTROL_AILESI_BASLIGI[aile]}</Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {config.izinTurleri.map((tur) => {
                const hucreler = kontrolMatrisiHucreleri(
                  config.kontrolMatrisiSatirlari,
                  workplaceId,
                  tur.id,
                );
                return (
                  <Table.Tr key={tur.id} data-testid={`kontrol-satiri-${tur.id}`}>
                    <Table.Td>{tur.ad}</Table.Td>
                    {PTW_KONTROL_AILELERI.map((aile) => {
                      const hucre = hucreler[aile];
                      const konum =
                        workplaceId === null ? hucre.holding : (hucre.isyeri ?? 'devral');
                      const testId = `kontrol-hucre-${tur.id}-${aile}`;
                      return (
                        <Table.Td key={aile}>
                          <Stack gap={2}>
                            <SegmentedControl
                              size="xs"
                              data-testid={testId}
                              data-konum={konum}
                              data-gecerli={hucre.gecerli}
                              aria-label={`${tur.ad} — ${PTW_KONTROL_AILESI_ETIKETI[aile]}`}
                              disabled={!yazabilir || yaz.isPending}
                              value={konum}
                              onChange={(yeni) => hucreDegistir(tur, aile, yeni, hucre)}
                              data={
                                workplaceId === null
                                  ? [
                                      { value: 'acik', label: DEGER_ETIKETI.acik },
                                      { value: 'kapali', label: DEGER_ETIKETI.kapali },
                                    ]
                                  : [
                                      { value: 'devral', label: 'Devral' },
                                      { value: 'acik', label: DEGER_ETIKETI.acik },
                                      { value: 'kapali', label: DEGER_ETIKETI.kapali },
                                    ]
                              }
                            />
                            {workplaceId !== null && konum === 'devral' && (
                              <Text size="xs" c="dimmed" data-testid={`${testId}-devral`}>
                                Holding: {DEGER_ETIKETI[hucre.holding]}
                              </Text>
                            )}
                          </Stack>
                        </Table.Td>
                      );
                    })}
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </div>
        <Modal opened={bekleyen !== null} onClose={kapat} title="Aileyi açmadan önce">
          <Stack gap="sm" data-testid="kontrol-maliyet">
            {maliyet.isPending && <Loader size="sm" />}
            {maliyet.isError && (
              <Text size="sm" c={ANLAM.engel} role="alert" data-testid="kontrol-maliyet-hata">
                {maliyet.error.message}
              </Text>
            )}
            {maliyet.data && bekleyen && (
              <>
                <Text size="sm" data-testid="kontrol-maliyet-metni">
                  {aileMaliyetMetni(maliyet.data, bekleyen.turAd)}
                </Text>
                <Text size="xs" c="dimmed" data-testid="kontrol-maliyet-kapsam">
                  {maliyet.data.calisanSayisi} aktif çalışan ·{' '}
                  {workplaceId === null
                    ? 'tüm aktif işyerleri'
                    : (config.isyerleri.find((isyeri) => isyeri.id === workplaceId)?.ad ?? '')}
                </Text>
              </>
            )}
            <Group justify="flex-end">
              <Button variant="default" onClick={kapat} data-testid="kontrol-maliyet-vazgec">
                Vazgeç
              </Button>
              <Button
                data-testid="kontrol-maliyet-onayla"
                disabled={!maliyet.data}
                loading={yaz.isPending}
                onClick={() => {
                  if (bekleyen) yaz.mutate(bekleyen.girdi, { onSettled: kapat });
                }}
              >
                Aç
              </Button>
            </Group>
          </Stack>
        </Modal>
        {yaz.error && (
          <Text size="sm" c={ANLAM.engel} role="alert" data-testid="kontrol-matrisi-hata">
            {yaz.error.message}
          </Text>
        )}
      </Stack>
    </Card>
  );
}

/**
 * AYAR DEFTERİ (K-73g) — her matris ve anahtar değişikliği ayrı satır. Adlar
 * ve değer metni SUNUCUDAN (ekran metin üretmez); sayfalı (P4).
 */
export function AyarDefteri({
  anahtar,
  baslik,
  testId,
}: {
  anahtar: string | null;
  baslik: string;
  testId: string;
}): React.JSX.Element {
  const [sayfa, setSayfa] = useState(1);
  const defter = useAyarDefteri(anahtar, sayfa);
  return (
    <Card withBorder data-testid={testId}>
      <Stack gap="xs">
        <Text fw={600}>{baslik}</Text>
        <QueryStatus queries={[defter]} />
        {defter.isSuccess && defter.data.toplam === 0 && (
          <Text size="sm" c="dimmed" data-testid={`${testId}-bos`}>
            Değişiklik kaydı yok.
          </Text>
        )}
        {defter.isSuccess && defter.data.toplam > 0 && (
          <>
            <div style={{ overflowX: 'auto' }}>
              <Table withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Zaman</Table.Th>
                    <Table.Th>Ayar</Table.Th>
                    <Table.Th>Kapsam</Table.Th>
                    <Table.Th>Eski → yeni</Table.Th>
                    <Table.Th>Kim</Table.Th>
                    <Table.Th>Rol</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {defter.data.satirlar.map((satir) => (
                    <Table.Tr key={satir.id} data-testid={`${testId}-satiri`}>
                      <Table.Td>{zamanGoster(satir.zaman)}</Table.Td>
                      <Table.Td>
                        {satir.anahtarEtiketi}
                        {satir.izinTuruAd && satir.aile && (
                          <Text size="xs" c="dimmed">
                            {satir.izinTuruAd} · {PTW_KONTROL_AILESI_ETIKETI[satir.aile]}
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>{satir.kapsam === 'holding' ? 'Holding' : satir.isyeriAd}</Table.Td>
                      <Table.Td data-testid={`${testId}-degisim`}>
                        {satir.eskiMetin} → {satir.yeniMetin}
                      </Table.Td>
                      <Table.Td>{satir.kimAd ?? '—'}</Table.Td>
                      <Table.Td>{satir.sapka ? SAPKA_ETIKET[satir.sapka] : '—'}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </div>
            <Sayfalama
              sayfa={sayfa}
              sayfaBoyutu={defter.data.sayfaBoyutu}
              toplam={defter.data.toplam}
              onDegis={setSayfa}
              testId={`${testId}-sayfalama`}
            />
          </>
        )}
      </Stack>
    </Card>
  );
}

/** Türkçe bulunma eki (sayı + iyelik): 7'sinde · 10'unda · 3'ünde. */
function sayiIyelikBulunma(sayi: number): string {
  const birler = [
    'ında',
    'inde',
    'sinde',
    'ünde',
    'ünde',
    'inde',
    'sında',
    'sinde',
    'inde',
    'unda',
  ];
  const onlar = ['', 'unda', 'sinde', 'unda', 'ında', 'sinde', 'ında', 'inde', 'inde', 'ında'];
  const ek = sayi % 10 === 0 && sayi !== 0 ? onlar[Math.floor(sayi / 10) % 10] : birler[sayi % 10];
  return `${sayi}'${ek}`;
}

/**
 * MUSLUK PANOSU (bulgu 75) — "Aktif olmayan kontroller" bloğunun iş izni
 * kısmı. Y4 aynen: yorum yok, öneri yok, uyarı rengi yok — yalnız SAYAR.
 * Her satır ilgili ekrana bağdır; ⚠️ bağ YALNIZ o ekranı görebilene çizilir
 * (görünür bağ + 403 sayfa = §2.40'ın en kötü kolu).
 */
export function PtwKontrolOzetiSatirlari({
  ozet,
}: {
  ozet: PtwKontrolOzetiDto;
}): React.JSX.Element {
  const authz = useMyAuthorization();
  const ptwYapilandirma = authz.isSuccess && yuzeyGorunur(authz.data, 'ptwYapilandirma');
  const sozlukler = authz.isSuccess && menuSatiriGorunur(authz.data.sapkalar, 'sozlukler');
  const yukleniciler = authz.isSuccess && menuSatiriGorunur(authz.data.sapkalar, 'yukleniciler');
  const bag = (metin: string, yol: string, gorunur: boolean): React.ReactNode =>
    gorunur ? (
      <Anchor component={Link} to={yol} size="sm">
        {metin}
      </Anchor>
    ) : (
      metin
    );
  const matris = '/izinler/yapilandirma';
  return (
    <Stack gap={4} data-testid="pano-ptw">
      {ozet.aileler.map((satir) => (
        <Text key={satir.aile} size="sm" data-testid={`pano-aile-${satir.aile}`}>
          {bag(
            `${PTW_KONTROL_AILESI_BASLIGI[satir.aile]} kontrolü: ${satir.turSayisi} türün ${sayiIyelikBulunma(satir.kapaliTurSayisi)} kapalı`,
            matris,
            ptwYapilandirma,
          )}
        </Text>
      ))}
      <Text size="sm" data-testid="pano-gevseyen">
        {bag(
          `${ozet.gevseyenIsyeriSayisi} işyeri holding ayarını gevşetmiş`,
          matris,
          ptwYapilandirma,
        )}
      </Text>
      <Text size="sm" data-testid="pano-katilimci">
        {bag('İş izni katılımcı kontrolü — kontrol matrisinden yönetilir', matris, ptwYapilandirma)}
      </Text>
      <Text size="sm" data-testid="pano-gorev-sarti">
        {bag(
          `Görev → yetkinlik şartı: ${ozet.gorevYetkinlikSartiSayisi}`,
          '/sozluk-bakim/izin_gorevi',
          sozlukler,
        )}
      </Text>
      <Text size="sm" data-testid="pano-zorunlu-gorev">
        {bag(
          `Tür → zorunlu görev: ${ozet.zorunluGorevTurSayisi}/${ozet.turSayisi} türde`,
          matris,
          ptwYapilandirma,
        )}
      </Text>
      <Text size="sm" data-testid="pano-onay-zinciri">
        {bag(
          `Onay zinciri: ${ozet.onayZinciriIsyeriSayisi}/${ozet.aktifIsyeriSayisi} işyerinde`,
          '/izinler/onay-zincirleri',
          ptwYapilandirma,
        )}
      </Text>
      {/* ✏️ K-102a/K-102d (17.09.2026): zorunluluk sözlükte değil PAKETTE — pano paketi sayar. */}
      <Text size="sm" data-testid="pano-evrak">
        {bag(
          `Yüklenici evrak paketi: ${ozet.evrakPaketiSayisi}`,
          '/yukleniciler/evrak-paketleri',
          yukleniciler,
        )}
      </Text>
      <Text size="sm" data-testid="pano-evrak-ozel-ek">
        {bag(
          `${ozet.ozelEkliYukleniciSayisi} yüklenicide özel ek var`,
          '/yukleniciler',
          yukleniciler,
        )}
      </Text>
      <Text size="sm" data-testid="pano-kapi">
        {bag(`Kapı kontrolleri: ${ozet.kapiKontrolSayisi}`, matris, ptwYapilandirma)}
      </Text>
    </Stack>
  );
}
